import { useState, useEffect } from 'react';
import { useStore, AI_PLAN_LIMITS } from '../../store/useStore';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { Brain, Loader2, Sparkles } from 'lucide-react';
import { AIProviderGrid } from './AIProviderGrid';
import { APIKeyForm } from './APIKeyForm';
import { AIProviderId, AI_PROVIDERS, APIKeys } from '../../lib/ai/types';
import { encryptKey, decryptKey } from '../../lib/ai/crypto';

export function AIProviderSettings() {
  const { currentUser, isRefreshingCredits, refreshCredits } = useStore();
  const subscriptionTier = currentUser?.subscriptionTier || 'Free';
  const [loading, setLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<AIProviderId>('local');
  const [keys, setKeys] = useState<APIKeys>({});
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [testingKeys, setTestingKeys] = useState<Record<string, boolean>>({});
  const [testResults, setTestResults] = useState<Record<string, 'success' | 'error' | null>>({});
  const [testErrors, setTestErrors] = useState<Record<string, string>>({});
  const [smartRotateEnabled, setSmartRotateEnabled] = useState(false);
  const [fallbackEnabled, setFallbackEnabled] = useState(false);
  const [fallbackOrder, setFallbackOrder] = useState<string[]>(() => AI_PROVIDERS.map(p => p.id));

  // Sync credits on mount
  useEffect(() => {
    refreshCredits();
  }, []);

  const [countdown, setCountdown] = useState<string>('');

  useEffect(() => {
    if (!currentUser?.credits_reset_at) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const resetTime = new Date(currentUser.credits_reset_at!).getTime();
      const diff = resetTime - now;

      if (diff <= 0) {
        setCountdown('Resetting...');
        // Only refresh if we haven't already to avoid spam
        if (countdown !== 'Resetting...') refreshCredits();
      } else {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setCountdown(`Resets in ${hours}h ${minutes}m`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [currentUser?.credits_reset_at]);

  useEffect(() => {
    async function loadAISettings() {
      if (!currentUser?.id || !isSupabaseConfigured) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('preferred_api_provider, user_api_keys, smart_rotate_enabled, api_fallback_enabled, api_fallback_order')
          .eq('id', currentUser.id)
          .single();

        console.log('[Persistence] Loaded preferred_api_provider:', data?.preferred_api_provider);

        if (error) throw error;

        if (data) {
          const provider = (data.preferred_api_provider as AIProviderId) || 'local';
          setSelectedProvider(provider);
          useStore.getState().setPreferredApiProvider(provider);
          setSmartRotateEnabled(data.smart_rotate_enabled || false);
          setFallbackEnabled(data.api_fallback_enabled || false);
          if (data.api_fallback_order) {
            const savedOrder = data.api_fallback_order as string[];
            const allProviderIds = AI_PROVIDERS.map(p => p.id);
            const missingProviders = allProviderIds.filter(id => !savedOrder.includes(id));
            setFallbackOrder([...savedOrder, ...missingProviders]);
          } else {
            setFallbackOrder(AI_PROVIDERS.map(p => p.id));
          }
          
          if (data.user_api_keys) {
            console.log('[Load] Profile fetched - user_api_keys:', data.user_api_keys, 'preferred_api_provider:', data.preferred_api_provider);
            const decryptedKeys: APIKeys = {};
            Object.entries(data.user_api_keys as Record<string, string | string[]>).forEach(([p, val]) => {
              console.log(`[Settings] Loaded keys for ${p}`);
              if (Array.isArray(val)) {
                decryptedKeys[p] = val.map(v => decryptKey(v, currentUser.id));
              } else if (typeof val === 'string' && val) {
                decryptedKeys[p] = [decryptKey(val, currentUser.id)];
              }
            });
            setKeys(decryptedKeys);
          } else {
            console.log('[Load] No user_api_keys found in profile');
          }
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
        toast.error('Failed to sync intelligence settings');
      } finally {
        setLoading(false);
      }
    }

    loadAISettings();
  }, [currentUser?.id]);

  const handleSave = async (params: { newKeys?: APIKeys, newRotate?: boolean, newFallback?: boolean, newProvider?: AIProviderId, newFallbackOrder?: string[] } = {}) => {
    if (!currentUser?.id || !isSupabaseConfigured) return;
    
    try {
      const keysToSave = params.newKeys || keys;
      const rotateToSave = params.newRotate !== undefined ? params.newRotate : smartRotateEnabled;
      const fallbackToSave = params.newFallback !== undefined ? params.newFallback : fallbackEnabled;
      const providerToSave = params.newProvider || selectedProvider;
      const fallbackOrderToSave = params.newFallbackOrder || fallbackOrder;

      const encryptedKeys: Record<string, string | string[]> = {};
      
      Object.entries(keysToSave).forEach(([p, val]) => {
        if (Array.isArray(val)) {
          encryptedKeys[p] = val
            .filter(k => typeof k === 'string' && k)
            .map(k => encryptKey(k || '', currentUser.id));
        } else if (typeof val === 'string' && val) {
          encryptedKeys[p] = [encryptKey(val || '', currentUser.id)];
        }
      });

      await useStore.getState().updateAISettings({
        provider: providerToSave,
        keys: encryptedKeys,
        fallback: !!fallbackToSave,
        rotate: !!rotateToSave,
        fallbackOrder: fallbackOrderToSave
      });
      
      toast.success('Settings synchronized');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save AI configuration');
    }
  };

  return (
    <div className="space-y-8 ">
      {/* NEW: Neural Capacity Horizontal Dashboard */}
      <div className="bg-[var(--t-surface)] p-8 rounded-[2.5rem] border border-[var(--t-border)] shadow-2xl overflow-hidden relative group">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--t-primary)]/5 via-transparent to-transparent" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-black text-white flex items-center gap-3">
              <Brain className="text-yellow-500" size={28} />
              Neural Capacity Dashboard
            </h3>
            {currentUser?.credits_reset_at && (
              <div className="flex items-center gap-3 text-xs font-black text-[var(--t-text-muted)] bg-[var(--t-bg)] p-3 px-5 rounded-full border border-[var(--t-border)] shadow-md">
                <Loader2 size={14} className="animate-spin text-[var(--t-primary)]" />
                <span className="uppercase tracking-widest text-[var(--t-primary)]">{countdown || 'Calculating...'}</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Stat 1: Remaining */}
            <div className="p-6 rounded-3xl bg-[var(--t-bg)] border border-[var(--t-border)] relative group/stat hover:border-[var(--t-primary)] transition-all duration-300 shadow-[inset_0_4px_30px_rgba(0,0,0,0.1)]">
              <p className="text-xs font-black text-[var(--t-text-muted)] uppercase tracking-widest mb-2">Remaining Credits</p>
              <div className="flex items-baseline justify-between">
                <p className="text-4xl font-black text-white tabular-nums">{(currentUser?.credits_remaining || 0).toLocaleString()}</p>
                <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 group-hover/stat:scale-110 transition-transform">
                  <Sparkles className="text-blue-400" size={24} />
                </div>
              </div>
              <div className="mt-4 w-full h-2 bg-white/5 rounded-full overflow-hidden p-0.5">
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 shadow-[0_0_10px_rgba(var(--t-primary-rgb),0.5)] transition-all duration-1000"
                  style={{ width: `${Math.min(100, ((currentUser?.credits_remaining || 0) / (AI_PLAN_LIMITS[subscriptionTier || 'Free'] || 50)) * 100)}%` }}
                />
              </div>
            </div>

            {/* Stat 2: Usage */}
            <div className="p-6 rounded-3xl bg-[var(--t-bg)] border border-[var(--t-border)] flex flex-col justify-center gap-1 group/stat hover:border-[var(--t-success)] transition-all duration-300 shadow-[inset_0_4px_30px_rgba(0,0,0,0.1)]">
              <p className="text-xs font-black text-[var(--t-text-muted)] uppercase tracking-widest">Today's Usage</p>
              <div className="flex items-baseline justify-between">
                <p className="text-4xl font-black text-white tabular-nums">{(currentUser?.total_credits_used_today || 0).toLocaleString()}</p>
                {isRefreshingCredits ? (
                  <span className="text-[10px] font-black text-[var(--t-success)] bg-[var(--t-success-dim)]/20 px-2 py-1 rounded-md uppercase tracking-wider animate-pulse">Syncing...</span>
                ) : (
                  <span className="text-[10px] font-black text-[var(--t-text-muted)] bg-white/5 px-2 py-1 rounded-md uppercase tracking-wider">Ready</span>
                )}
              </div>
            </div>

            {/* Stat 3: Daily Cap */}
            <div className="p-6 rounded-3xl bg-[var(--t-bg)] border border-[var(--t-border)] flex flex-col justify-center gap-1 group/stat hover:border-white/20 transition-all duration-300 shadow-[inset_0_4px_30px_rgba(0,0,0,0.1)]">
                <p className="text-xs font-black text-[var(--t-text-muted)] uppercase tracking-widest">Daily Cap</p>
                <div className="flex items-baseline justify-between">
                  <p className="text-4xl font-black text-white/50 tabular-nums">{(AI_PLAN_LIMITS[subscriptionTier || 'Free'] || 50).toLocaleString()}</p>
                  <span className="text-[10px] font-black text-white/20 uppercase tracking-widest">{subscriptionTier} Plan</span>
                </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-10">
          <div className="space-y-10">
            {/* Provider Selection */}
            <div className="bg-[var(--t-surface)] p-10 rounded-[2.5rem] border border-[var(--t-border)] shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-[var(--t-primary)] to-transparent opacity-50" />
              <h3 className="text-2xl font-black text-white mb-8 flex items-center gap-3">
                <Sparkles className="text-[var(--t-primary)]" size={24} />
                Neural Engines
              </h3>
              <AIProviderGrid 
                providers={AI_PROVIDERS} 
                selectedId={selectedProvider} 
                onSelect={(id) => {
                  setSelectedProvider(id);
                  useStore.getState().setPreferredApiProvider(id);
                  if (typeof window !== 'undefined') {
                    localStorage.setItem('wholescale-preferred-provider', id);
                  }
                  // Trigger sync
                  handleSave({ newProvider: id });
                }} 
              />
            </div>

            {/* API Key Form & Advanced Controls */}
            <APIKeyForm 
              providers={AI_PROVIDERS}
              keys={keys}
              setKeys={setKeys}
              showKeys={showKeys}
              setShowKeys={setShowKeys}
              testingKeys={testingKeys}
              setTestingKeys={setTestingKeys}
              testResults={testResults}
              setTestResults={setTestResults}
              testErrors={testErrors}
              setTestErrors={setTestErrors}
              saveSettings={handleSave}
              smartRotateEnabled={smartRotateEnabled}
              setSmartRotateEnabled={(val) => {
                setSmartRotateEnabled(val);
                handleSave({ newRotate: val });
              }}
              fallbackEnabled={fallbackEnabled}
              setFallbackEnabled={(val) => {
                setFallbackEnabled(val);
                handleSave({ newFallback: val });
              }}
              fallbackOrder={fallbackOrder}
              setFallbackOrder={(val) => {
                setFallbackOrder(val);
                handleSave({ newFallbackOrder: val });
              }}
            />
        </div>
      </div>
    </div>
  );
}
